import { IncidentsLayer } from "./MapLayers/IncidentsLayer";
import { ParkingSpotsLayer } from "./MapLayers/ParkingSpotsLayer";
import { TsrLocationsLayer } from "./MapLayers/TsrLocationsLayer";
import { FsrLocationsLayer } from "./MapLayers/FsrLocationsLayer";
import { DirectionsControl } from "./Routing/DirectionsControl";
import { PanToGeoLocation } from "./Controls/PanToGeoLocation";
import { AutoCompleteBox } from "./Controls/AutoCompleteBox";
import { MapController } from "./MapController";
import { SafetyRouteController } from "./SafetyRouting/SafetyRouteController";
import { RiskAssessmentController } from "./RiskAssessment/RiskAssessmentController";

export class Map {
  constructor(googleMaps, mapElement) {
    this.googleMaps = googleMaps;
    // eslint-disable-next-line no-undef
    this.infoWindow = new google.maps.InfoWindow();
    this.startLatitude = 54.8;
    this.startLongitude = 9.6;
    // eslint-disable-next-line no-undef
    this.mapCenter = new google.maps.LatLng(this.startLatitude, this.startLongitude);
    this.mapOptions = {
      zoomControl: true,
      zoomControlOptions: {
        // eslint-disable-next-line no-undef
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      mapTypeControl: true,
      mapTypeControlOptions: {
        // eslint-disable-next-line no-undef
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        // eslint-disable-next-line no-undef
        position: google.maps.ControlPosition.BOTTOM_CENTER,
        mapTypeIds: [
          // eslint-disable-next-line no-undef
          google.maps.MapTypeId.ROADMAP,
          // eslint-disable-next-line no-undef
          google.maps.MapTypeId.HYBRID,
        ],
      },
      scaleControl: true,
      streetViewControl: true,
      rotateControl: false,
      zoom: 8,
      mapId: "6065b1e3a081a13f",
      center: this.mapCenter,
    };
    this.loading = 0;

    this.dataLayers = {
      incidents: {},
      parkingSpots: {},
      tsrLocations: {},
      fsrLocations: {},
    };
    this.mapHtmlElement = mapElement;
    this.apiUrls = {
      incidents: this.mapHtmlElement.dataset.apiIncidentUrl,
      incidentsExport: this.mapHtmlElement.dataset.apiIncidentExportUrl,
      parkingSpots: this.mapHtmlElement.dataset.apiParkingspotsUrl,
      parkingSpotsExport: this.mapHtmlElement.dataset.apiParkingspotsExportUrl,
      tsrLocations: this.mapHtmlElement.dataset.apiTsrlocationsUrl,
      tsrLocationsExport: this.mapHtmlElement.dataset.apiTsrlocationsExportUrl,
      fsrLocations: this.mapHtmlElement.dataset.apiFsrlocationsUrl,
      fsrLocationsExport: this.mapHtmlElement.dataset.apiFsrlocationsExportUrl,
      saveBookmarks: this.mapHtmlElement.dataset.apiSavebookmarkUrl,
      getBookmarks: this.mapHtmlElement.dataset.apiGetBookmarks,
      deleteBookmark: this.mapHtmlElement.dataset.apiDeleteBookmark,
      assess: this.mapHtmlElement.dataset.apiAssessUrl,
      assessment: this.mapHtmlElement.dataset.apiAssessmentUrl,
      assessmentReport: this.mapHtmlElement.dataset.apiAssessmentReportUrl,
      assessmentReportStatus: this.mapHtmlElement.dataset.apiAssessmentReportStatusUrl,
      assessmentPdf: this.mapHtmlElement.dataset.apiAssessmentPdfUrl,
      csrfToken: this.mapHtmlElement.dataset.csrfToken,
    };
    // 'usable' | 'consent' | 'disabled' — whether the LLM report may be generated (see map/index.php).
    this.assistantState = this.mapHtmlElement.dataset.assistantState || "disabled";
    this.controls = {
      panToGeoLocation: {},
      autoCompleteBox: {},
    };
  }

  create() {
    window.addEventListener("oneMoreMapItemLoading", () => {
      this.moreLoading();
    });
    window.addEventListener("oneLessMapItemLoading", () => {
      this.lessLoading();
    });

    /**
     * TODO:
     * trigger data reload by dispatching CustomEvent (typo is intended)
     * window.dispatchEvent(new CustomEvent("reloadData", { detail: { layerName: "IncidentsLayer" } }));
     * window.dispatchEvent(new CustomEvent("reloadData", { detail: { layerName: "TsrLocationsLaye" } }));
     * window.dispatchEvent(new CustomEvent("reloadData", { detail: { layerName: "FsrLocationsLaye" } }));
     * window.dispatchEvent(new CustomEvent("reloadData", { detail: { layerName: "ParkingSpotsLayer" } }));
     */

    // eslint-disable-next-line no-undef
    this.map = new google.maps.Map(this.mapHtmlElement, this.mapOptions);

    this.dataLayers.incidents = new IncidentsLayer(
      this.map,
      this.apiUrls.incidents,
      this.apiUrls.incidentsExport,
      this.apiUrls.csrfToken
    );
    this.dataLayers.parkingSpots = new ParkingSpotsLayer(
      this.map,
      this.apiUrls.parkingSpots,
      this.apiUrls.parkingSpotsExport,
      this.apiUrls.csrfToken
    );
    this.dataLayers.tsrLocations = new TsrLocationsLayer(
      this.map,
      this.apiUrls.tsrLocations,
      this.apiUrls.tsrLocationsExport,
      this.apiUrls.csrfToken
    );
    this.dataLayers.fsrLocations = new FsrLocationsLayer(
      this.map,
      this.apiUrls.fsrLocations,
      this.apiUrls.fsrLocationsExport,
      this.apiUrls.csrfToken
    );

    // eslint-disable-next-line no-undef
    const trafficLayer = new google.maps.TrafficLayer();
    // Exposed so the assistant facade (window.tapaMap) can read/toggle traffic state.
    this.trafficLayer = trafficLayer;

    const toggleTrafficLayer = document.createElement("button");
    toggleTrafficLayer.id = "toggleTrafficLayerButton";
    toggleTrafficLayer.type = "button";
    toggleTrafficLayer.style.margin = "10px 0 0 10px";
    toggleTrafficLayer.classList = "btn btn-light";
    toggleTrafficLayer.innerHTML = "Toggle TrafficLayer";

    toggleTrafficLayer.addEventListener("click", (event) => {
      event.preventDefault();
      trafficLayer.setMap(trafficLayer.getMap() ? null : this.map);
      toggleTrafficLayer.classList.toggle("active");
    });

    // eslint-disable-next-line no-undef
    this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(toggleTrafficLayer);

    // Create routing
    const directionsDiv = document.getElementById("directions");
    // eslint-disable-next-line no-undef
    google.maps.event.addListenerOnce(this.map, "idle", () => {
      // Kept on the instance so the assistant facade can plan/clear routes programmatically.
      this.directionsControl = new DirectionsControl(directionsDiv, this.map, this.apiUrls);
      // Safety routing builds on the DirectionsControl and the data layers, so it is created last.
      this.safetyRouteController = new SafetyRouteController(this);
      // Route risk assessment reads the renderer's active route; no-op when the assessment tab is absent.
      this.riskAssessmentController = new RiskAssessmentController(this);
    });

    // controls
    this.controls.panToGeoLocation = new PanToGeoLocation(this.map, this.infoWindow);
    this.controls.panToGeoLocation.createControl();

    this.controls.autoCompleteBox = new AutoCompleteBox(this.map, this.infoWindow);
    this.controls.autoCompleteBox.createControl();

    // Expose a stable control surface for the AI assistant (see Assistant.executeMapActions).
    window.tapaMap = new MapController(this);
  }

  moreLoading() {
    this.loading += 1;
    if (this.loading > 0) {
      const loadingIndicator = document.querySelector("#map-loading-indicator");
      loadingIndicator.classList.remove("inactive");
      loadingIndicator.classList.add("active");
    }
  }
  lessLoading() {
    if (this.loading > 0) {
      this.loading -= 1;
    }
    if (this.loading === 0) {
      const loadingIndicator = document.querySelector("#map-loading-indicator");
      loadingIndicator.classList.remove("active");
      loadingIndicator.classList.add("inactive");
    }
  }
}
